/**
 * Scene lifecycle state machine shared across the demo scenes.
 *
 * <p>Pixi 8's {@code Application#init()} is async; React 19 strict mode mounts
 * components twice in dev. The combination causes a race: a scene starts
 * initialising, then strict-mode unmounts and calls {@code destroy()} before
 * {@code init()} resolves. The state machine lets each scene check after the
 * await whether it should bail out and clean up, rather than crash trying to
 * append a canvas to a now-detached DOM container.
 */
export type SceneState = 'created' | 'initializing' | 'initialized' | 'destroyed';

/**
 * Compares scene state without TypeScript flow-narrowing.
 *
 * <p>After {@code this.state = 'initializing'} TS narrows {@code this.state}
 * to the literal {@code 'initializing'} and treats {@code this.state ===
 * 'destroyed'} as never-true — even though a strict-mode unmount can mutate
 * it during the subsequent await. Wrapping the comparison in a free function
 * passes through a fresh parameter of type {@link SceneState}, so the
 * narrowing doesn't reach the comparison.
 */
export function isDestroyed(state: SceneState): boolean {
  return state === 'destroyed';
}
